<template>
  <div>
    <BaseDialog
      v-model="dialog"
      :disabled="inputAttrs.disabled"
      :max-width="maxWidth"
      :return-value="itemValue"
      :hasCancel="$vuetify.breakpoint.xsOnly"
      :hasAction="$vuetify.breakpoint.smAndUp"
      :clearable="$attrs['clearable'] || true"
      @continue="dialog = false"
    >
      <template #activator="{ attrs, on }">
        <!-- refactor to $attrs instead of inputAttrs -->
        <!-- REFACTOR: fix mouseup still opening dialog if select is disabled without abusing inputAttrs, remove check if on.click exists -->
        <Select
          readonly
          clearable
          :autofocus="$attrs['autofocus']"
          v-bind="Object.assign(attrs, inputAttrs)"
          v-on="on"
          @keydown.native="handleKeydownOnSelect"
          @mouseup="e => (on.click && !inputAttrs.disabled ? on.click(e) : e => {})"
          @click:clear="clearValue"
          :value="selectedObject"
          :items="items"
          :item-value="itemValue"
          :prepend-inner-icon="
            ((!selectedObject || alwaysShowIcon) && ( icon || 'mdi-magnify')) || null
          "
          :placeholder="localSearchText"
          :loading="loading"
          :class="{ 'd-none': !visible }"
        >
          <!-- how to pass/override slots: https://gist.github.com/loilo/73c55ed04917ecf5d682ec70a2a1b8e2 -->
          <!-- pass through scopedSlots with their scope data -->
          <template
            v-for="(_, scopedSlotName) in $scopedSlots"
            #[scopedSlotName]="slotData"
          >
            <slot :name="scopedSlotName" v-bind="slotData" />
          </template>

          <!-- pass through normal slots -->
          <template v-for="(_, slotName) in $slots" #[slotName]>
            <slot :name="slotName" />
          </template>

          <template #selection="{ item }">
            <slot
              name="selection"
              :item="item"
              :avatarComplexProps="finalisedAvatarComplexProps"
            >
              <AvatarComplex
                :object="item"
                :title="get(item, itemText)"
                x-small
                v-bind="{ ...finalisedAvatarComplexProps, }"
              />
            </slot>
          </template>

          <template #prepend-item>
            <v-text-field
              v-model="query"
              autofocus
              hide-details
              :hint="
                $t('general.x_from_y_results', {
                  x: items.length,
                  y: recordCount,
                })
              "
              prepend-inner-icon="mdi-magnify"
              :placeholder="localSearchText"
              :loading="loading"
            />
          </template>
        </Select>
      </template>
      <template #title>
        <div class="d-flex align-center">
          <IndexFilter
            v-if="showFilterButton"
            :showBadgeOfSelectedFilters="showBadgeOfSelectedFilters"
            :numberOfSelectedFilters="numberOfSelectedFilters"
            @click:filterPanel="filterPanelOpen = !filterPanelOpen"
          />
          <div class="ml-2"/>
          <IndexSorting
            v-if="showSortButton"
            v-model="localParams.sort"
            :items="sorts"
          />
        </div>
        <div class="my-3"></div>
        <v-text-field
          v-model="query"
          ref="searchbox"
          autofocus
          hide-details
          prepend-inner-icon="mdi-magnify"
          :placeholder="localSearchText"
          :loading="loading"
        />
      </template>
      <template #subtitle>
        <div class="d-flex align-center mt-n2">
          <div>
            {{
              $t('general.x_from_y_results', { x: items.length, y: recordCount })
            }}
          </div>
          <div>
            <v-btn icon @click="refreshSearch">
              <v-icon small :class="{ 'spin-icon': isSpinning }">mdi-cached</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
      <template #content>
        <v-list ref="list">
          <!--  TODO: remove list item requirement for item slot -->
          <v-list-item
            class="my-0 listitem disablePadding px-2"
            v-for="(item, index) in items"
            :key="`item-${index}`"
            :class="{ 'listitem--selected': itemSelected(item), 'listitem--active': index == activeIndex}"
            @click="setValue(item.id)"
          >
            <slot
              name="item"
              :item="item"
              :selected="itemSelected(item)"
              :avatarComplexProps="finalisedAvatarComplexProps"
              @click="setValue(item.id)"
            >  
              <span class="ml-2" style="margin-top: 1px">
                <AvatarComplex
                  show-all-lines
                  disable-padding
                  :object="item"
                  :title="get(item, itemText)"
                  :subtitle="get(item, itemTextSubtitle)"
                  :additionalSubtitle="get(item, itemTextAdditionalSubtitle)"
                  v-bind="{ ...finalisedAvatarComplexProps, }"
                />
              </span>
            </slot>
          </v-list-item>
        </v-list>
        <IndexInfiniteScroller
          v-bind="{ recordCount }"
          :page.sync="localParams.page"
          :errors="errors"
        />
      </template>
    </BaseDialog>

    <div
      v-if="createRoutes && createRoutes.length > 0"
      class="d-flex flex-wrap"
      style="column-gap: 16px;"
    >
      <div
        v-for="(createRouteObject, i) in createRoutes"
        :key="'create-route-button' + i"
        class="d-flex mt-3"
        style="gap: 8px;"
      >
        <div>
          <a @click="goToCreate(createRouteObject)">
            <v-icon small>mdi-plus-circle</v-icon>
            {{ createRouteObject.text || 'create' }}
          </a>
        </div>
      </div>
    </div>

    <IndexFilterFields
      v-if="filterFields && filterFields.length > 0"
      :filters="localParams.filter"
      :filter-fields="normalizedFilterFields"
      :reset-value="filters"
      @reset:filters="lazyFilters && setFilters($event)"
      @submit="lazyFilters && setFilters($event)"
      @update:filters="!lazyFilters && setFilters($event)"
    />
  </div>
</template>

<script>

import localValue from '@/mixins/localValue';
import { translateFilter } from '@/functions/translationHelpers';
import { debounce, uniqBy, merge, get, intersection } from 'lodash';
import { get as pathifyGet, sync } from 'vuex-pathify';
import handleErrors from '@/functions/handleErrors';
import IndexInfiniteScroller from '@/components/IndexInfiniteScroller.vue';
import AvatarComplex from '@/components/AvatarComplex.vue';
import IndexFilter from '@/components/IndexFilter.vue';
import IndexSorting from '@/components/IndexSorting.vue';
import IndexFilterFields from '@/components/IndexFilterFields.vue';
import FILTER_FIELDS_MAP from '@/constants/filterFieldsMap';

export default {
  name: 'SearchInput',
  mixins: [localValue],
  components: {
    IndexInfiniteScroller,
    AvatarComplex,
    IndexFilter,
    IndexSorting,
    IndexFilterFields,
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    indexServiceAction: {
      type: Function,
      required: true,
    },
    indexServiceParams: {
      type: Object,
      default: () => ({}),
    },
    initialServiceAction: {
      type: Function,
      required: true,
    },
    searchScope: {
      type: String,
      default: 'withName',
    },
    limit: {
      type: Number,
      default: 20,
    },
    maxWidth: {
      type: Number,
      default: 500,
    },
    placeholder: {
      type: String,
      default: '',
    },
    alwaysShowIcon: {
      type: Boolean,
      default: false,
    },
    inputAttrs: {
      // pass props to the select component
      type: Object,
      default: () => ({}),
    },
    visible: {
      // needed because v-show applies to the dialog, but the activator element stays visible
      type: Boolean,
      default: true,
    },
    icon: {
      type: String,
      default: undefined,
    },
    lazyLoadObject: {
      type: Object,
      required: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    /*
      :createRoute="
        { 
          route: {
            name: 'AdminShiploadUserNew',
            query: {
              shiploadUserType: 'captain',
              accountId: this.formData?.shippingCompanyId,
            }
          },
          text: this.$t('views.shipload.create_captain_user'),
        }"
    */
    createRoute: {
      // Displays a link to the create route (object)
      type: [Object, Array],
      required: false,
    },
    currentSortOption: {
      type: String,
      default: '',
    },
    generatedByForm: {
      type: Boolean,
      default: false,
    },
    avatarComplexProps: {
      type: Object,
      default: () => ({}),
    },

    // ----- FILTERS -----
    sorts: {
      type: Array,
      default: () => [],
    },
    filterFields: {
      /* see '@/constants/filterFieldMap.js'
        { 
          name: String<BackendFilterId>, 
          label: String/<$t.key>, 
          component?: VueComponent 
        }[]
      */
      type: Array,
      default: () => [],
    },
    filters: {
      // active filters object, Object with {filter_name: value}
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    lazyFilters: {
      // Set to true to do a new query on submitting filters instead of updating.
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      initialScrollToSelectedObject: false,
      activeIndex: -1,
      dialog: false,
      selectedObject: undefined,
      loading: false,
      query: '',
      localParams: {
        filter: {},
        sort: '',
        page: {
          offset: 0,
          limit: 20,
        },
      },
      results: [],
      errors: [],
      meta: undefined,
      debouncedSearch: debounce((query, returnResults, mode) => {
        return new Promise((resolve) => {
          this.search(query, returnResults, mode).then(resolve);
        });
      }, Math.floor(Math.random() * 5 + 1) * 150), // Random multiple of 150 between 150-750ms
      initialized: false,
      isSpinning: false,
      spinDuration: 1000, //1 second of spin animation
    };
  },
  watch: {
    query: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal || oldVal) {
          this.debouncedSearch(newVal, false, 'search');
        }
      },
    },
    indexServiceParams: {
      immediate: false,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.debouncedSearch(this.query, false, 'search');
        }
      },
    },
    dialog: {
      handler(val) {
        if (val && this.recordCount === 0) {
          this.debouncedSearch(this.query, false, 'search');
        }
        this.initialScrollToSelectedObject = val;
        if (val) {
          window.addEventListener('keydown', this.handleKeyDown);
          this.activeIndex = this.items.findIndex(item => item?.id == this.selectedObject?.id)
        } else {
          this.query = '';
          this.activeIndex = -1;
          window.removeEventListener('keydown', this.handleKeyDown);
        }
      },
    },
    localValue: {
      immediate: true,
      handler(newVal, oldVal) {
        if (!newVal) {
          this.setSelectedObject(undefined);
        } else if (newVal != oldVal) {
          this.getSelectedObject(newVal);
        }
      },
    },
    indexServiceAction: {
      handler() {
        this.debouncedSearch(this.query, false, 'search');
      },
    },
    selectedObject(object) {
      var eventName = 'change:object'

      this.$emit(eventName, object);
      let inputVal = object?.[this.itemValue] || '';
      this.$emit('input', inputVal);
    },
    reactiveLocalParams: {
      async handler(newVal, oldVal) {
        if (!oldVal) { // First run, or params reset from outside
          this.debouncedSearch(this.query, false, 'search');
          return;
        }

        const filterChanged = JSON.stringify(newVal.filter) !== JSON.stringify(oldVal.filter);
        if (filterChanged) return; // Skip if filter changed - this is already handled by setFilters

        const sortChanged = newVal.sort !== oldVal.sort;
        const limitChanged = newVal.page.limit !== oldVal.page.limit;
        const offsetChanged = newVal.page.offset !== oldVal.page.offset;

        if (sortChanged || limitChanged) {
          // 'search' mode in debouncedSearch will handle resetting offset and fetching new data.
          this.debouncedSearch(this.query, false, 'search');
        } else if (offsetChanged) {
          // If only offset changed (e.g. infinite scroll), append new items.
          this.loadItems();
        }
      },
    },
    activeIndex: {
      handler(newVal, oldVal) {
        if (newVal == oldVal || newVal == -1) return;

        this.scrollToActiveItem(newVal);
      },
    },
  },
  computed: {
    currentRouteResource: pathifyGet('app/currentRouteResource'),
    localSearchText() {
      return this.placeholder || this.$t('general.search') + '...';
    },
    itemText() {
      // REFACTOR: use inputAttrs instead of this
      return this.$attrs['item-text'] || this.$attrs['itemText']  || 'name';
    },
    itemTextSubtitle() {
      // REFACTOR: use inputAttrs instead of this
      return this.$attrs['item-text-subtitle'] || this.$attrs['itemTextSubtitle']  || undefined;
    },
    itemTextAdditionalSubtitle() {
      // REFACTOR: use inputAttrs instead of this
      return this.$attrs['item-text-additional-subtitle'] || this.$attrs['itemTextAdditionalSubtitle']  || undefined;
    },
    itemValue() {
      // REFACTOR: use inputAttrs instead of this
      return this.$attrs['item-value'] || this.$attrs['itemValue'] || 'id';
    },
    items() {
      // Cache items array to prevent unnecessary re-renders
      let items = [...this.results];
      if (this.selectedObject) {
        items.push(this.selectedObject);
      }
      return uniqBy(items, 'id');
    },
    recordCount() {
      return this.meta?.recordCount || 0;
    },
    reactiveLocalParams() {
      // always returns new object when localParams changes
      // needed for comparing old and new deep values between objects in watcher
      return Object.assign({}, this.localParams);
    },
    createRoutes() {
      if (!this.createRoute) return [];

      return !Array.isArray(this.createRoute) ? [this.createRoute] : this.createRoute;
    },
    finalisedAvatarComplexProps() {
      let DEFAULT_AVATAR_COMPLEX_PROPS = {
        generatedByForm: this.generatedByForm,
        disablePadding: true,
        disableAvatar: this.generatedByForm,
        // avatarSize: '20', //
        // smallInitials: '10' //
      };

      return this.merge(DEFAULT_AVATAR_COMPLEX_PROPS, this.avatarComplexProps);
    },
    showSortButton() {
      return (this.sorts || []).length > 0;
    },
    showFilterButton() {
      return (this.filterFields || []).length > 0;
    },
    normalizedFilterFields() {
      return this.filterFields.map(field => this.normalizeField(field));
    },
    filterFieldNames() {
      return this.normalizedFilterFields.map(x => x.name);
    },
    currentSortOptionApplied() {
      if (this.currentSortOption) {
        return this.translateSortOption(this.currentSortOption);
      } else {
        return null;
      }
    },
    /*  
    Check if programatically applied filters also exist in normal user-shown filters.
    This avoids the subindex bug where selected filters number shows 1 even 
    though no filters were user-selected. This is due to the 
    e.g. "with_project_id" filter programmatically applied which the program
    detected as a normal filter and as such is incorrectly showing 1 filter applied 
    when in reality the user did not apply any filter 
    */
    numberOfSelectedFilters() {
      const filterKeys = Object.keys(this.localParams?.filter || {});
      const normalizedFilterNames = this.normalizedFilterFields.map(field => field.name);
      return intersection(filterKeys, normalizedFilterNames).length;
    },
    showBadgeOfSelectedFilters() {
      return this.numberOfSelectedFilters > 0;
    },
    filterPanelOpen: sync('rightPanel/isOpen'),
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    merge,
    get,
    handleErrors,
    clearValue() {
      this.setValue(null);
      this.$emit('click:clear');
    },
    refreshSearch() {
      this.isSpinning = true;
      this.debouncedSearch(this.query, false, 'search')
        .finally(() => {
          setTimeout(() => {
            this.isSpinning = false;
          }, this.spinDuration); //duration of animation class
        });
    },
    scrollToActiveItem(index = this.activeIndex) {
      this.$nextTick(() => {
        /*
          Use parent container ref 'list' instead of creating ref for each single result
          Note that this functionality could bug if extra html (e.g. styling) elements are inserted under v-list
          in the same level as results (v-list-item)
        */
        const activeItem = this.$refs.list?.$children[index];

        if (activeItem) {
          var scrollBehavior = this.initialScrollToSelectedObject  ? 'instant' : 'smooth';
          activeItem.$el?.scrollIntoView({behavior: scrollBehavior, block: "center", inline: "nearest"});
          this.initialScrollToSelectedObject = false;
        }
      });
    },
    handleKeydownOnSelect(event) {
      // Open dialog when searchInput select is in focus
      if (!this.dialog && !this.inputAttrs?.disabled && this.isCharacterKeyPress(event))
        this.dialog = true;
    },
    handleKeyDown(event) {
      // Check what key is pressed
      if (event.key === 'ArrowUp')
        this.onArrowUp();
      else if (event.key === 'ArrowDown')
        this.onArrowDown();
      else if (event.key === 'Enter')
        this.onEnter();
      else if (this.isCharacterKeyPress(event))
        this.$refs.searchbox?.focus();
    },
    onArrowDown() {
      if (this.activeIndex + 1 < this.items?.length)
        this.activeIndex = this.activeIndex + 1;
      else
        //dont go further down when at end of current results but still have more API results to load. Otherwise, go to first result 
        this.activeIndex = this.items?.length < this.recordCount ? this.activeIndex : 0;
    },
    onArrowUp() {
      this.activeIndex = this.activeIndex - 1 >= 0 ? this.activeIndex - 1 : this.items?.length - 1;
    },
    onEnter() {
      if (this.items?.length > 0 && this.activeIndex >= 0)
        this.setValue(this.items[this.activeIndex]?.id);
    },
    // https://stackoverflow.com/a/4180715
    isCharacterKeyPress(event) {
      if (typeof event.which == "undefined") {
          // This is IE, which only fires keypress events for printable keys
          return true;
      } else if (typeof event.which == "number" && event.which > 0) {
          // In other browsers except old versions of WebKit, event.which is
          // only greater than zero if the keypress is a printable key.
          // We need to filter out backspace and ctrl/alt/meta key combinations
          return event.key !== 'Tab' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.which != 8;
      }
      return false;
    },
    async loadItems() {
      const data = await this.search(this.query, true);
      this.updateItems(data);
    },
    async getSelectedObject(id) {
      if (!id) return;
      this.loading = true;
      if (this.lazyLoadObject && !this.initialized) {
        // Use lazy load object when initializing
        this.setSelectedObject(this.lazyLoadObject);
        this.loading = false;
      } else {
        try {
          const { data } = await this.initialServiceAction(id);
          this.setSelectedObject(data);
        } catch (e) {
          this.errors = this.handleErrors(e);
        } finally {
          this.loading = false;
        }
      }
    },
    setValue(val) {
      this.dialog = false;
      this.localValue = val;
    },
    setSelectedObject(object) {
      var eventName = this.initialized ? 'object:change' : 'object:initialize';

      this.$emit(eventName, object);
      this.initialized = true;
      this.selectedObject = object;
    },
    itemSelected(item) {
      return this.selectedObject?.id === item?.id;
    },
    clearSelected() {
      this.dialog = false;
      this.setValue('');
    },
    async search(query = '', returnResults = false, mode = undefined) {
      this.loading = true;
      // Merge params
      const serviceFilters = JSON.parse(JSON.stringify(this.indexServiceParams?.filter || {}));
      query = query?.trim() || '';
      let params = {
        ...this.indexServiceParams,
      };

      // Delete filter from params to handle it separately
      delete params.filter;

      if (mode == 'search')
        this.localParams.page.offset = 0;

      params = {
        ...params,
        ...this.localParams,
      };
      // Ensure sort criteria is not null for the API call
      if (params.sort === null) {
        params.sort = '';
      }
      // Properly merge filters
      params.filter = {
        ...serviceFilters,           // First add service filters
        ...this.localParams.filter,  // Then add local filters (may override service filters)
        [this.searchScope]: query || ''  // Finally add search query
      };
      try {
        const { data, meta } = await this.indexServiceAction(params);
        this.meta = meta;
        if (returnResults)
          return data;
        else
          this.results = data;

        if (mode === 'search') {
          // For sort, query text, filter changes: always reset to top, no special scroll.
          this.initialScrollToSelectedObject = false;
          this.activeIndex = this.items?.length > 0 ? 0 : -1;
        } else {
          // For other cases (e.g., initial dialog load with a pre-selection and empty query)
          if (this.selectedObject && query === '') {
            this.initialScrollToSelectedObject = true; // Allow instant scroll to selected
            const foundIndex = this.items.findIndex(item => item?.id === this.selectedObject?.id);
            this.activeIndex = foundIndex !== -1 ? foundIndex : (this.items.length > 0 ? 0 : -1);
          } else {
            // Default to top if no selectedObject or if query is not empty
            this.initialScrollToSelectedObject = false;
            this.activeIndex = this.items.length > 0 ? 0 : -1;
          }
        }
        this.$emit('collection:results', this.results);
      } catch (e) {
        this.errors = this.handleErrors(e);
      } finally {
        this.loading = false;
      }
    },
    updateItems(items = []) {
      this.results.push(...items);
      if (this.results.length > 0)
        this.results = uniqBy(this.results, 'id');
    },
    goToCreate(createRouteObject) {
      let routeData = this.$router.resolve(createRouteObject?.route);
      window.open(routeData.href, '_blank');
    },
    normalizeField(field) {
      // always coerce to objects
      if (typeof field === 'string') field = { name: field };

      // Determine the field key for inheritance and component
      let inheritanceKey = field.name.replace(/_id$/, '');
      let componentKey = field.as || inheritanceKey;

      // Get inherited field
      const inheritedField = this.filterFields.find(x => x?.name === inheritanceKey) || FILTER_FIELDS_MAP[inheritanceKey] || {};

      // Get component field
      let componentField = FILTER_FIELDS_MAP[componentKey] || {};

      // If no component field found and type is defined, try with 'type_' prefix
      if (!componentField.component && field.type !== undefined) {
        const typeComponentKey = `type_${field.type}`;
        componentField = FILTER_FIELDS_MAP[typeComponentKey] || {};
      }

      // Merge the fields, giving priority to the current field
      field = merge({}, inheritedField, componentField, field);

      //auto-assign label to placeholder if no placeholder was given
      field['label'] = field.label || translateFilter(field.name, this.config?.resource || this.currentRouteResource);
      field['placeholder'] = field.placeholder || field.label;
      
      return field;
    },
    setFilters(filters) {
      this.$set(this.localParams, 'filter', filters);
      if (this.$vuetify.breakpoint.xsOnly) {
        this.filterPanelOpen = false;
      }
      this.debouncedSearch(this.query, false, 'search');
    },
    translateSortOption(item) {
      const key = item
        .replace('-', '')
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase();
      let direction = 'asc';
      if (item[0] === '-') {
        direction = 'desc';
      }
      const keyText = this.$t(`general.sort.option.${key}`);
      const directionText = this.$t(`general.sort.direction.${direction}`);
      return `${keyText} (${directionText})`;
    },
  },
};
</script>
<style lang="css" scoped>
  /* spin animation for refresh button */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .spin-icon {
    animation: spin 1s linear;
  }
  
  /deep/ .v-select__selections {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
  }
  /deep/ .v-select__selections > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /deep/ .v-input__slot {
    margin-bottom: 0px !important;
  }
  /deep/ .v-input__slot > * {
    align-self: center !important;
    margin-top: 0px !important;
  }
  /deep/ [id^="input-"]:not(:first-child) {
    display:none;
  }
  /deep/ .v-text-field {
    padding-top: 0px !important;
    margin-top: 0px !important;
  }
  /deep/ .v-text-field__details {
    display: none;
  }
</style>
<style lang="scss" scoped>
.applied-sort {
  opacity: 0.4;
}
.searchfield__listwrapper {
  padding-top: 16px;
  max-height: 450px;
}
.listwrapper {
  overflow-y: auto;
}
.listitem {
  border-radius: 4px;
  cursor: pointer;
  background-color: transparent;
  transition: background-color ease 0.2s;

  &#{&}:hover {
    // exceed selectivity over selected
    background-color: rgba(0, 0, 0, 0.06);
  }
  &.listitem--selected {
    border: 1px solid rgba(233, 92, 35, 0.70);
    background-color: rgba(233, 92, 35, 0.06) !important;
  }
  &.listitem--active {
    background-color: rgba(0, 0, 0, 0.06) !important;
  }
}
.inner-listitem {
  border: 0px;
}
</style>